import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Camera, Filter, X, FileText, ChevronDown } from 'lucide-react';

const STATUS_COLORS = {
  sent: 'bg-green-500/20 text-green-400',
  pending: 'bg-amber-500/20 text-amber-400',
  failed: 'bg-red-500/20 text-red-400',
  not_required: 'bg-white/10 text-gray-300',
};

const TYPE_LABELS = {
  project: 'Project',
  multi_project: 'Multi-Project',
  general: 'General Overhead',
};

function formatRelativeDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const date = new Date(isoStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  } catch {
    return isoStr.slice(0, 10);
  }
}

export function MyCaptures() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [captures, setCaptures] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [status, setStatus] = useState('all');
  const [company, setCompany] = useState('all');
  const [destination, setDestination] = useState('all');
  const [captureType, setCaptureType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const buildParams = useCallback((offset = 0) => {
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    if (company !== 'all') params.set('company', company);
    if (destination !== 'all') params.set('destination', destination);
    if (captureType !== 'all') params.set('type', captureType);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('limit', '50');
    params.set('offset', String(offset));
    return params.toString();
  }, [status, company, destination, captureType, dateFrom, dateTo]);

  const loadCaptures = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/captures/my-captures?${buildParams(0)}`);
      setCaptures(res.data.captures || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load captures');
    } finally {
      setLoading(false);
    }
  }, [api, buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/captures/my-captures?${buildParams(captures.length)}`);
      setCaptures(prev => [...prev, ...(res.data.captures || [])]);
    } catch {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { loadCaptures(); }, [loadCaptures]);

  const clearFilters = () => {
    setStatus('all'); setCompany('all'); setDestination('all');
    setCaptureType('all'); setDateFrom(''); setDateTo('');
  };

  const hasFilters = status !== 'all' || company !== 'all' || destination !== 'all' || captureType !== 'all' || dateFrom || dateTo;

  if (!user?.capture_enabled) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <Card className="bg-[#2A3240] border-white/10 p-8 text-center">
          <Camera className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-white font-medium">Capture access not enabled</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 pb-28 max-w-3xl mx-auto" data-testid="my-captures-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-white">My Captures</h1>
        <p className="text-sm text-gray-400">{total} capture{total !== 1 ? 's' : ''}</p>
      </div>

      {/* Filter toggle (mobile) + filters */}
      <div className="mb-4">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white sm:hidden mb-2"
          data-testid="toggle-filters"
        >
          <Filter className="w-4 h-4" />
          Filters
          <ChevronDown className={`w-3 h-3 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
          {hasFilters && <span className="w-2 h-2 rounded-full bg-[#F5A623]" />}
        </button>

        <div className={`${filtersOpen ? 'block' : 'hidden'} sm:block`}>
          <div className="flex flex-wrap gap-2 items-end">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[120px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All Status</SelectItem>
                <SelectItem value="sent" className="text-white">Sent</SelectItem>
                <SelectItem value="failed" className="text-white">Failed</SelectItem>
                <SelectItem value="pending" className="text-white">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger className="w-[120px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-company">
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All</SelectItem>
                <SelectItem value="CON" className="text-white">CON</SelectItem>
                <SelectItem value="DEV" className="text-white">DEV</SelectItem>
              </SelectContent>
            </Select>
            <Select value={destination} onValueChange={setDestination}>
              <SelectTrigger className="w-[120px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-destination">
                <SelectValue placeholder="Dest" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All</SelectItem>
                <SelectItem value="livecosts" className="text-white">LiveCosts</SelectItem>
                <SelectItem value="xero" className="text-white">Xero</SelectItem>
              </SelectContent>
            </Select>
            <Select value={captureType} onValueChange={setCaptureType}>
              <SelectTrigger className="w-[120px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All Types</SelectItem>
                <SelectItem value="project" className="text-white">Project</SelectItem>
                <SelectItem value="multi_project" className="text-white">Multi-Project</SelectItem>
                <SelectItem value="general" className="text-white">General</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-date-from" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9" data-testid="filter-date-to" />
            {hasFilters && (
              <button onClick={clearFilters} className="text-xs text-[#F5A623] hover:underline flex items-center gap-1" data-testid="clear-filters">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
      ) : captures.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="py-12 text-center">
            {hasFilters ? (
              <>
                <Filter className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No captures match these filters</p>
                <button onClick={clearFilters} className="text-[#F5A623] text-sm mt-2 hover:underline" data-testid="clear-filters-empty">Clear filters</button>
              </>
            ) : (
              <>
                <Camera className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No captures yet</p>
                <Button onClick={() => navigate('/portal/capture')} className="mt-3 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="first-capture-btn">
                  Take your first capture
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {captures.map(c => (
            <Card
              key={c.id}
              className="bg-[#2A3240] border-white/5 hover:border-white/15 cursor-pointer transition-colors"
              onClick={() => navigate(`/portal/my-captures/${c.id}`)}
              data-testid={`capture-card-${c.id}`}
            >
              <CardContent className="p-3 flex items-start gap-3">
                {/* Thumbnail */}
                <div className="w-12 h-12 rounded-lg bg-[#1E2530] border border-white/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-red-400" />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {c.capture_type === 'project' ? `${c.linked_project_ref || ''} ${c.linked_project_name || ''}`.trim() || 'Project' : TYPE_LABELS[c.capture_type] || c.capture_type}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-gray-400 text-xs capitalize">{c.destination_type}</span>
                    <Badge className={`text-[10px] px-1.5 py-0 ${c.company === 'CON' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>{c.company}</Badge>
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5">{formatRelativeDate(c.created_at)}</p>
                  {c.sent_status !== 'sent' && c.failure_reason && (
                    <p className="text-amber-300/90 text-xs mt-1 truncate" data-testid={`my-capture-reason-${c.id}`}>{c.failure_reason}</p>
                  )}
                </div>
                {/* Status */}
                <Badge className={`${STATUS_COLORS[c.sent_status]} text-[10px] flex-shrink-0`} data-testid={`my-capture-status-${c.id}`}>{c.details_only ? 'details only' : c.sent_status === 'sent' ? 'sent' : 'not sent'}</Badge>
              </CardContent>
            </Card>
          ))}

          {captures.length < total && (
            <Button
              onClick={loadMore}
              disabled={loadingMore}
              variant="outline"
              className="w-full border-white/10 text-gray-400 hover:bg-white/5"
              data-testid="load-more-btn"
            >
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Load more ({total - captures.length} remaining)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
